import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { format } from 'date-fns'
import { supabase } from '@/lib/supabase'
import {
  APP_SETTINGS_QUERY_KEY,
  APP_SETTINGS_STREAMING_CATALOGUE_WINDOW_DAYS_KEY,
} from '@/config/appSettingsKeys'
import { STREAMING_FILTER_OPTIONS_QUERY_KEY } from '@/hooks/useStreamingListings'
import { ADMIN_STREAMING_REPORT_QUERY_KEY } from '@/hooks/useAdminStreamingReport'
import { ADMIN_PORTAL_PATH } from '@/lib/adminPortal'
import { AdminSectionCard } from '@/components/listings-management/AdminSectionCard'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const STREAMING_HEALTH_QUERY_KEY = ['admin-streaming-listings-health']
const ENRICH_LIMIT = 30

interface StreamingSettingsSectionProps {
  sessionToken: string | undefined
}

export function StreamingSettingsSection({ sessionToken }: StreamingSettingsSectionProps) {
  const queryClient = useQueryClient()
  const [windowDaysInput, setWindowDaysInput] = useState('7')
  const [windowDaysMessage, setWindowDaysMessage] = useState<string | null>(null)
  const [clearMessage, setClearMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [enrichLoading, setEnrichLoading] = useState(false)
  const [enrichResult, setEnrichResult] = useState<{ updated: number; processed: number; message?: string } | null>(
    null
  )
  const [enrichError, setEnrichError] = useState<string | null>(null)
  const [enrichTitle, setEnrichTitle] = useState('')

  const { data: streamingWindowSetting } = useQuery({
    queryKey: [...APP_SETTINGS_QUERY_KEY, APP_SETTINGS_STREAMING_CATALOGUE_WINDOW_DAYS_KEY],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('app_settings')
        .select('value')
        .eq('key', APP_SETTINGS_STREAMING_CATALOGUE_WINDOW_DAYS_KEY)
        .maybeSingle()
      if (error) return null
      return data?.value ?? null
    },
    staleTime: 60_000,
  })

  useEffect(() => {
    if (streamingWindowSetting == null || streamingWindowSetting === '') return
    const n = parseInt(String(streamingWindowSetting), 10)
    if (Number.isFinite(n) && n >= 1 && n <= 365) setWindowDaysInput(String(n))
  }, [streamingWindowSetting])

  const saveWindowDaysMutation = useMutation({
    mutationFn: async () => {
      const n = parseInt(windowDaysInput, 10)
      if (!Number.isFinite(n) || n < 1 || n > 365) throw new Error('Window days must be between 1 and 365.')
      const { error } = await supabase.from('app_settings').upsert(
        {
          key: APP_SETTINGS_STREAMING_CATALOGUE_WINDOW_DAYS_KEY,
          value: String(n),
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'key' }
      )
      if (error) throw error
      return n
    },
    onSuccess: (n) => {
      queryClient.invalidateQueries({ queryKey: APP_SETTINGS_QUERY_KEY })
      setWindowDaysMessage(`Saved default window: ${n} day(s) from start of today UTC.`)
      setTimeout(() => setWindowDaysMessage(null), 5000)
    },
    onError: (err: Error) => {
      setWindowDaysMessage(err.message)
      setTimeout(() => setWindowDaysMessage(null), 8000)
    },
  })

  const clearExpiredMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.rpc('clear_expired_streaming_listings')
      if (error) throw error
      return Number(data ?? 0)
    },
    onSuccess: (deleted) => {
      setClearMessage({ type: 'success', text: `Removed ${deleted} expired row(s).` })
      queryClient.invalidateQueries({ queryKey: STREAMING_HEALTH_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: ['streaming-listings'] })
      queryClient.invalidateQueries({ queryKey: STREAMING_FILTER_OPTIONS_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: ADMIN_STREAMING_REPORT_QUERY_KEY })
      setTimeout(() => setClearMessage(null), 8000)
    },
    onError: (err: Error) => {
      setClearMessage({ type: 'error', text: err.message })
      setTimeout(() => setClearMessage(null), 10000)
    },
  })

  const clearAllMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.rpc('clear_all_streaming_listings')
      if (error) throw error
      return Number(data ?? 0)
    },
    onSuccess: (deleted) => {
      setClearMessage({ type: 'success', text: `Removed all ${deleted} streaming listing row(s).` })
      queryClient.invalidateQueries({ queryKey: STREAMING_HEALTH_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: ['streaming-listings'] })
      queryClient.invalidateQueries({ queryKey: STREAMING_FILTER_OPTIONS_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: ADMIN_STREAMING_REPORT_QUERY_KEY })
      setTimeout(() => setClearMessage(null), 8000)
    },
    onError: (err: Error) => {
      setClearMessage({ type: 'error', text: err.message })
      setTimeout(() => setClearMessage(null), 10000)
    },
  })

  const { data: health, isLoading: healthLoading, isError: healthError } = useQuery({
    queryKey: STREAMING_HEALTH_QUERY_KEY,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_admin_streaming_listings_health')
      if (error) throw error
      const row = Array.isArray(data) ? data[0] : data
      return row as {
        row_count: number | null
        last_run_at: string | null
        last_run_rows_processed: number | null
        last_run_error: string | null
      } | null
    },
    staleTime: 30_000,
    retry: false,
  })

  const runEnrich = useCallback(async () => {
    if (!sessionToken || !supabaseUrl) return
    setEnrichLoading(true)
    setEnrichError(null)
    setEnrichResult(null)
    try {
      const res = await fetch(`${supabaseUrl}/functions/v1/enrich_streaming_tmdb`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${sessionToken}`,
        },
        body: JSON.stringify({
          limit: ENRICH_LIMIT,
          ...(enrichTitle.trim() ? { title: enrichTitle.trim() } : {}),
        }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data?.error ?? `HTTP ${res.status}`)
      setEnrichResult(data)
      queryClient.invalidateQueries({ queryKey: ['streaming-listings'] })
    } catch (e) {
      setEnrichError(e instanceof Error ? e.message : 'Request failed')
    } finally {
      setEnrichLoading(false)
    }
  }, [sessionToken, queryClient, enrichTitle])

  return (
    <div id="streaming" className="space-y-6">
      <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
        Streaming catalogue CSV files are ingested from Google Drive inbox folders when Refresh listings runs (n8n).
        No direct CSV upload here.{' '}
        <Link className="underline" style={{ color: 'var(--color-focus)' }} to={`${ADMIN_PORTAL_PATH}/streaming-report`}>
          Streaming report
        </Link>
      </p>

      <AdminSectionCard id="streaming-window" title="Catalogue visibility window">
        <p className="mb-3 text-sm" style={{ color: 'var(--text-secondary)' }}>
          How long each ingested row stays on the public Streaming page, from start of today (UTC).
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            Days (1–365)
            <input
              type="number"
              min={1}
              max={365}
              value={windowDaysInput}
              onChange={(e) => setWindowDaysInput(e.target.value)}
              className="block mt-1 w-24 min-h-[44px] px-3 rounded border-2 text-sm"
              style={{ borderColor: 'var(--bg-tertiary)', color: 'var(--text-primary)', backgroundColor: 'var(--bg-primary)' }}
            />
          </label>
          <button
            type="button"
            onClick={() => saveWindowDaysMutation.mutate()}
            disabled={saveWindowDaysMutation.isPending}
            className="px-4 py-2 rounded text-sm font-semibold"
            style={{ backgroundColor: 'var(--bg-interactive)', color: 'var(--text-inverse)' }}
          >
            {saveWindowDaysMutation.isPending ? 'Saving…' : 'Save as default'}
          </button>
        </div>
        {windowDaysMessage && (
          <p className="mt-2 text-sm" style={{ color: 'var(--text-primary)' }} role="status">
            {windowDaysMessage}
          </p>
        )}
      </AdminSectionCard>

      <AdminSectionCard id="streaming-maintenance" title="Catalogue maintenance">
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => clearExpiredMutation.mutate()}
            disabled={clearExpiredMutation.isPending || clearAllMutation.isPending}
            className="px-4 py-2 rounded text-sm font-semibold border"
            style={{ borderColor: 'var(--bg-tertiary)', color: 'var(--text-primary)', backgroundColor: 'var(--bg-primary)' }}
          >
            {clearExpiredMutation.isPending ? 'Clearing…' : 'Clear expired listings'}
          </button>
          <button
            type="button"
            onClick={() => {
              if (!window.confirm('Delete ALL streaming_listings rows? This cannot be undone.')) return
              clearAllMutation.mutate()
            }}
            disabled={clearExpiredMutation.isPending || clearAllMutation.isPending}
            className="px-4 py-2 rounded text-sm font-semibold"
            style={{ backgroundColor: 'var(--color-error)', color: '#fff' }}
          >
            {clearAllMutation.isPending ? 'Clearing…' : 'Clear all streaming listings'}
          </button>
        </div>
        {clearMessage && (
          <p className="mt-3 text-sm" role="alert" style={{ color: clearMessage.type === 'error' ? 'var(--color-error)' : 'var(--text-primary)' }}>
            {clearMessage.text}
          </p>
        )}
      </AdminSectionCard>

      {healthLoading ? (
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }} role="status">
          Loading catalogue health…
        </p>
      ) : healthError ? (
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
          Catalogue health unavailable. Apply migration 074.
        </p>
      ) : health != null ? (
        <AdminSectionCard id="streaming-health" title="Catalogue health">
          <dl className="grid gap-2 sm:grid-cols-2 text-sm">
            <div>
              <dt className="font-medium" style={{ color: 'var(--text-secondary)' }}>Listings count</dt>
              <dd style={{ color: 'var(--text-primary)' }}>{health.row_count?.toLocaleString() ?? '—'}</dd>
            </div>
            <div>
              <dt className="font-medium" style={{ color: 'var(--text-secondary)' }}>Last ingest</dt>
              <dd style={{ color: 'var(--text-primary)' }}>
                {health.last_run_at ? format(new Date(health.last_run_at), 'dd MMM yyyy, HH:mm') : '—'}
                {health.last_run_rows_processed != null && health.last_run_at && (
                  <span className="ml-1" style={{ color: 'var(--text-secondary)' }}>
                    ({health.last_run_rows_processed} rows)
                  </span>
                )}
              </dd>
            </div>
            {health.last_run_error && (
              <div className="sm:col-span-2">
                <dt className="font-medium" style={{ color: 'var(--text-secondary)' }}>Last ingest error</dt>
                <dd className="mt-1 p-2 rounded text-sm" style={{ backgroundColor: 'var(--bg-primary)', color: 'var(--color-error)' }}>
                  {health.last_run_error}
                </dd>
              </div>
            )}
          </dl>
        </AdminSectionCard>
      ) : null}

      <AdminSectionCard id="streaming-tmdb" title="TMDB poster enrichment">
        <p className="mb-3 text-sm" style={{ color: 'var(--text-secondary)' }}>
          Fetch poster/backdrop URLs from TMDB for streaming listings missing artwork (batch of {ENRICH_LIMIT}).
        </p>
        <div className="flex flex-wrap gap-3 mb-3">
          <label className="text-sm flex-1 min-w-[12rem]" style={{ color: 'var(--text-secondary)' }}>
            Optional title filter
            <input
              type="text"
              value={enrichTitle}
              onChange={(e) => setEnrichTitle(e.target.value)}
              className="block mt-1 w-full min-h-[44px] px-3 rounded border-2 text-sm"
              style={{ borderColor: 'var(--bg-tertiary)', color: 'var(--text-primary)', backgroundColor: 'var(--bg-primary)' }}
            />
          </label>
          <button
            type="button"
            onClick={runEnrich}
            disabled={enrichLoading || !sessionToken}
            className="px-4 py-2 self-end rounded text-sm font-semibold"
            style={{ backgroundColor: 'var(--bg-interactive)', color: 'var(--text-inverse)' }}
          >
            {enrichLoading ? 'Enriching…' : 'Run TMDB enrich'}
          </button>
        </div>
        {enrichError && (
          <p className="text-sm" style={{ color: 'var(--color-error)' }} role="alert">
            {enrichError}
          </p>
        )}
        {enrichResult && (
          <p className="text-sm" style={{ color: 'var(--text-primary)' }} role="status">
            Updated {enrichResult.updated} of {enrichResult.processed} processed.
            {enrichResult.message && ` ${enrichResult.message}`}
          </p>
        )}
      </AdminSectionCard>
    </div>
  )
}
